import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Button, Empty, Input, Modal, Pagination, Popconfirm, Segmented, Select, Spin, Tag, Upload, message } from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  HighlightOutlined,
  PictureOutlined,
  PlusOutlined,
  ScanOutlined,
  ThunderboltOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import './DesignCreatorPage.css';

const { TextArea } = Input;

interface TextItem { id: string; text: string; confidence?: number; to?: string }
interface Task {
  id: string;
  type: string;
  status: string;
  prompt?: string;
  size?: string;
  assetId?: string;
  resultAssetId?: string;
  imageUrl?: string | null;
  imageModel?: string;
  ocrResult?: { texts?: TextItem[]; fullText?: string; error?: string };
  textEdits?: Array<{ from: string; to: string }>;
  editRequirement?: string;
  error?: string | null;
  createdAt?: string;
}

const sizeOptions = [
  { label: '16:9 横版', value: '16:9' },
  { label: '1:1 方图', value: '1:1' },
  { label: '4:3 标准', value: '4:3' },
  { label: '3:4 竖版', value: '3:4' },
  { label: '9:16 长图', value: '9:16' },
];

function withToken(url?: string | null) {
  if (!url) return '';
  const token = useAuthStore.getState().token;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token || '')}`;
}

const HISTORY_PAGE_SIZE = 10;

export default function DesignCreatorPage() {
  const [mode, setMode] = useState<'generate' | 'upload'>('generate');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('16:9');
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [history, setHistory] = useState<Task[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [textItems, setTextItems] = useState<TextItem[]>([]);
  const [editRequirement, setEditRequirement] = useState('');
  const [previewMode, setPreviewMode] = useState<'result' | 'source'>('result');
  const [error, setError] = useState('');
  const [draftTaskId, setDraftTaskId] = useState('');
  const [refImages, setRefImages] = useState<{uuid:string;name:string;uploadedAt:string;exists:boolean}[]>([]);
  const [refPreview, setRefPreview] = useState<{uuid:string;name:string} | null>(null);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  const [modelPreference, setModelPreference] = useState('default');
  const [modelOptions, setModelOptions] = useState<{value:string;label:string}[]>([]);
  const promptInputRef = useRef<any>(null);

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { loadModels(); }, []);

  useEffect(() => {
    if (!refImages.length || refPickerOpen) return;
    const cursorChar = prompt.slice(-1);
    if (cursorChar === '@' || cursorChar === '＠') setRefPickerOpen(true);
  }, [prompt, refImages.length, refPickerOpen]);

  const currentImage = useMemo(() => withToken(task?.imageUrl), [task?.imageUrl]);
  const canEdit = textItems.some(t => String(t.to || '').trim() && String(t.to || '').trim() !== t.text);

  const taskTitle = (item: Task) => {
    if (item.editRequirement) return item.editRequirement.slice(0, 20);
    if (item.type === 'image_edit_regenerate') return '需求改图版本';
    if (item.prompt) return item.prompt.slice(0, 20);
    if (item.type === 'upload_edit') return '上传图片改文字';
    return '文字修改版本';
  };

  const loadHistory = async (page = historyPage) => {
    try {
      const r = await api.get('/design-creator', { params: { page, pageSize: HISTORY_PAGE_SIZE } });
      const data = r.data;
      // 兼容旧版返回数组 / 新版返回 {items,total}
      if (Array.isArray(data)) {
        setHistory(data);
        setHistoryTotal(data.length);
      } else {
        setHistory(data?.items || []);
        setHistoryTotal(data?.total || 0);
      }
      setHistoryPage(page);
    } catch {}
  };

  const loadModels = async () => {
    try {
      const r = await api.get('/design-creator/models');
      const data: Array<{id:string; label:string}> = r.data || [];
      setModelOptions(data.map((m: any) => ({ value: m.id, label: m.label })));
    } catch (e) {
      console.error('加载生图模型列表失败:', e);
    }
  };

  // 生图接口已改为后台异步执行（长连接会被代理网关约30秒掐断），提交后轮询任务状态直到完成/失败
  const pollTask = async (id: string): Promise<Task> => {
    for (let i = 0; i < 240; i++) {
      await new Promise(res => setTimeout(res, 3000));
      let data: Task | undefined;
      try {
        const r = await api.get(`/design-creator/${id}`);
        data = r.data;
      } catch { continue; }
      if (data?.status === 'completed') return data;
      if (data?.status === 'failed') throw new Error(data?.error || '生成失败');
    }
    throw new Error('任务超时，请稍后在历史记录中查看结果');
  };

  const waitTaskDone = async (initial: Task): Promise<Task> =>
    initial?.status === 'processing' ? pollTask(initial.id) : initial;

  const handleGenerate = async () => {
    if (!prompt.trim()) { setError('请输入设计图提示词'); return; }
    setError(''); setLoading(true);
    try {
      const r = await api.post('/design-creator/generate', { prompt, size, draftTaskId: draftTaskId || undefined, modelPreference });
      const done = await waitTaskDone(r.data);
      setTask(done); setTextItems([]); setEditRequirement(''); setDraftTaskId(''); setRefImages([]); setMode('generate'); await loadHistory(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '生成失败');
    } finally { setLoading(false); }
  };

  const ensureDraft = async () => {
    if (draftTaskId) return draftTaskId;
    setError('');
    try {
      const r = await api.post('/design-creator', {});
      const id = r.data?.id;
      setDraftTaskId(id);
      setTask(r.data);
      return id;
    } catch (e: any) {
      setError(e?.response?.data?.message || '创建草稿失败');
      return '';
    }
  };

  const loadRefImages = async (taskId: string) => {
    try {
      const r = await api.get(`/design-creator/${taskId}/reference-images`);
      setRefImages(r.data || []);
    } catch {}
  };

  // 批量上传引用图片（支持多选）
  const handleUploadRefBatch = (file: File, fileList: File[]) => {
    // antd Upload 的 beforeUpload 对每个文件各调一次，
    // 只在最后一个文件到达时执行批量上传，避免重复
    if (file !== fileList[fileList.length - 1]) return;
    handleUploadRefs(fileList);
  };

  const handleUploadRefs = async (files: File[]) => {
    const id = await ensureDraft();
    if (!id) return;
    setError(''); setLoading(true);
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append('file', file);
        await api.post(`/design-creator/${id}/reference-images`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      } catch (e: any) {
        setError(`上传 ${file.name} 失败: ${e?.response?.data?.message || e?.message || '未知错误'}`);
      }
    }
    await loadRefImages(id);
    message.success(`已上传 ${files.length} 张引用图片`);
    setLoading(false);
  };

  const handleDeleteRef = async (uuid: string) => {
    if (!draftTaskId) return;
    try {
      await api.delete(`/design-creator/${draftTaskId}/reference-images/${uuid}`);
      await loadRefImages(draftTaskId);
    } catch {}
  };

  const openRefPickerIfPossible = () => {
    if (!refImages.length) {
      message.info('请先上传引用图片，再输入 @ 选择引用');
      return;
    }
    setRefPickerOpen(true);
  };

  const handlePromptChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value);
    const cursor = e.target.selectionStart || 0;
    const lastChar = cursor > 0 ? value[cursor - 1] : '';
    if (lastChar === '@' || lastChar === '＠') openRefPickerIfPossible();
  };

  const insertRefImage = (uuid: string) => {
    const el = promptInputRef.current?.resizableTextArea?.textArea;
    const marker = `@img:${uuid} `;
    if (!el) {
      setPrompt(prev => `${prev}${prev && !/\s$/.test(prev) ? ' ' : ''}${marker}`);
      setRefPickerOpen(false);
      return;
    }
    const rawStart = el.selectionStart || 0;
    const rawEnd = el.selectionEnd || rawStart;
    const prevChar = rawStart > 0 ? prompt[rawStart - 1] : '';
    const start = prevChar === '@' || prevChar === '＠' ? rawStart - 1 : rawStart;
    const prefix = start > 0 && !/\s/.test(prompt[start - 1] || '') ? ' ' : '';
    const next = prompt.slice(0, start) + prefix + marker + prompt.slice(rawEnd);
    const cursor = start + prefix.length + marker.length;
    setPrompt(next);
    setRefPickerOpen(false);
    setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = cursor; } catch {} }, 60);
  };

  const handleUpload = async (file: File) => {
    setError(''); setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/design-creator/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setTask(r.data); setTextItems([]); setEditRequirement(''); setMode('upload'); await loadHistory(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '上传失败');
    } finally { setLoading(false); }
  };

  const clearCurrent = () => {
    setTask(null);
    setTextItems([]);
    setEditRequirement('');
    setError('');
    setPreviewMode('result');
  };

  const handleDeleteCurrent = async () => {
    if (!task?.id) { clearCurrent(); return; }
    setError(''); setLoading(true);
    try {
      await api.delete(`/design-creator/${task.id}`);
      clearCurrent();
      await loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '删除失败');
    } finally { setLoading(false); }
  };

  const handleOcr = async () => {
    if (!task?.id) return;
    setError(''); setOcrLoading(true);
    try {
      const r = await api.post(`/design-creator/${task.id}/ocr`, {}, { timeout: 120000 });
      const texts = (r.data?.texts || []).map((t: TextItem) => ({ ...t, to: t.text }));
      setTextItems(texts);
      setTask(prev => prev ? { ...prev, ocrResult: r.data } : prev);
      if (r.data?.error) setError(r.data.error);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '文字识别失败');
    } finally { setOcrLoading(false); }
  };

  const handleApplyEdits = async () => {
    if (!task?.id) return;
    const edits = textItems.map(t => ({ from: t.text, to: String(t.to || '').trim() })).filter(e => e.from && e.to && e.from !== e.to);
    if (!edits.length) { setError('请先修改至少一条文字'); return; }
    setError(''); setEditLoading(true);
    try {
      const r = await api.post(`/design-creator/${task.id}/apply-text-edits`, { edits, modelPreference });
      const done = await waitTaskDone(r.data);
      setTask(done); setTextItems([]); setPreviewMode('result'); await loadHistory(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '应用文字修改失败');
    } finally { setEditLoading(false); }
  };

  const handleImageEdit = async () => {
    if (!task?.id) { setError('请先上传图片'); return; }
    const requirement = editRequirement.trim();
    if (!requirement) { setError('请输入图片修改需求'); return; }
    setError(''); setEditLoading(true);
    try {
      const r = await api.post(`/design-creator/${task.id}/image-edit`, { requirement, size, modelPreference });
      const done = await waitTaskDone(r.data);
      setTask(done); setTextItems([]); setPreviewMode('result'); await loadHistory(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '图片修改失败');
    } finally { setEditLoading(false); }
  };

  const handleDownload = () => {
    const assetId = task?.resultAssetId || task?.assetId;
    const token = useAuthStore.getState().token;
    if (!assetId || !token) return;
    window.open(`/api/design-creator/assets/${assetId}/download?token=${encodeURIComponent(token)}`, '_blank');
  };

  const selectHistory = async (item: Task) => {
    setError('');
    try {
      const r = await api.get(`/design-creator/${item.id}`);
      setTask(r.data);
      const texts = (r.data?.ocrResult?.texts || []).map((t: TextItem) => ({ ...t, to: t.text }));
      setTextItems(texts);
      setEditRequirement(r.data?.editRequirement || '');
      setMode(r.data?.type === 'prompt_generate' ? 'generate' : 'upload');
    } catch (e: any) { setError(e?.response?.data?.message || '加载历史失败'); }
  };

  return (
    <div className="design-creator-page">
      <section className="dc-hero">
        <strong>设计图创作</strong>
      </section>

      <section className="dc-workbench">
        <aside className="dc-panel dc-control-panel">
          <Segmented block value={mode} onChange={(v) => setMode(v as any)} options={[{ label: 'AI生成设计图', value: 'generate' }, { label: '上传图片改文字', value: 'upload' }]} />

          {mode === 'generate' ? (
            <div className="dc-section">
              <label>设计图提示词</label>
              <div className="dc-prompt-wrap">
                <TextArea
                  ref={promptInputRef}
                  value={prompt}
                  onChange={handlePromptChange}
                  onKeyDown={(e) => {
                    if (e.key === '@' || e.key === '＠') setTimeout(openRefPickerIfPossible, 0);
                    if (e.key === 'Escape') setRefPickerOpen(false);
                  }}
                  autoSize={{ minRows: 6, maxRows: 10 }}
                  placeholder="例如：一个蓝色科技风登录页，标题是智坊，背景是粒子流。上传引用图后输入 @ 选择图片，系统会把引用图随请求提交给以图生图模型。"
                />
              </div>
              <div className="dc-ref-section">
                <label>引用图片（可选，最多8张）</label>
                <div className="dc-ref-list">
                  {refImages.map(ref => (
                    <div key={ref.uuid} className="dc-ref-row">
                      <span className="dc-ref-name" onClick={() => setRefPreview({uuid: ref.uuid, name: ref.name})}>{ref.name}</span>
                      <button className="dc-ref-del" onClick={() => handleDeleteRef(ref.uuid)}>×</button>
                    </div>
                  ))}
                  <Upload multiple accept="image/png,image/jpeg,image/jpg,image/webp" showUploadList={false}
                    beforeUpload={(file, fileList) => { handleUploadRefBatch(file, fileList); return false; }}
                    disabled={refImages.length >= 8 || loading}>
                    <div className="dc-ref-add-text">+ 添加引用图片</div>
                  </Upload>
                </div>
                <Modal open={!!refPreview} footer={null} onCancel={() => setRefPreview(null)} width="auto" title={refPreview?.name}>
                  {refPreview && <img src={`/api/design-creator/${draftTaskId}/reference-images/${refPreview.uuid}/view`} alt={refPreview.name} style={{maxWidth:'78vw',maxHeight:'78vh',borderRadius:12}} />}
                </Modal>
                <p className="dc-ref-hint">上传引用图后，在提示词中输入 @ 选择引用；如果已上传但未插入标记，后端也会默认把本次草稿中的引用图传给以图生图接口。</p>
                <Button className="dc-ref-select-btn" size="small" type="dashed" disabled={!refImages.length} onClick={openRefPickerIfPossible}>选择引用图片</Button>
              </div>
              <div className="dc-row">
                <span>画面尺寸</span>
                <Select value={size} onChange={setSize} options={sizeOptions} />
              </div>
              <div className="dc-row">
                <span>生图模型</span>
                <Select value={modelPreference} onChange={setModelPreference} options={modelOptions.length ? modelOptions : [{ value: 'default', label: '默认（自动切换）' }]} />
              </div>
              <Button type="primary" size="large" block icon={<ThunderboltOutlined />} loading={loading} onClick={handleGenerate}>生成设计图</Button>
            </div>
          ) : (
            <div className="dc-section">
              <label>上传本地图片</label>
              <Upload.Dragger accept="image/png,image/jpeg,image/jpg,image/webp" showUploadList={false} beforeUpload={(file) => { handleUpload(file); return false; }} disabled={loading}>
                <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
                <p className="ant-upload-text">拖拽图片到这里，或点击上传</p>
                <p className="ant-upload-hint">支持 PNG / JPG / WEBP，最大 20MB</p>
              </Upload.Dragger>
              {task?.id && mode === 'upload' && (
                <div className="dc-current-actions">
                  <span>当前已有一张处理中图片</span>
                  <Popconfirm
                    title="删除当前图片任务？"
                    description="删除后可重新上传新图片，历史中也不再显示这条任务。"
                    okText="删除"
                    cancelText="取消"
                    onConfirm={handleDeleteCurrent}
                  >
                    <Button danger icon={<DeleteOutlined />} loading={loading}>删除当前，重新上传</Button>
                  </Popconfirm>
                </div>
              )}
              <Button size="large" block icon={<ScanOutlined />} loading={ocrLoading} disabled={!task?.id || loading} onClick={handleOcr}>识别图中文字</Button>
              <div className="dc-image-edit-box">
                <label>直接输入修改需求</label>
                <TextArea
                  value={editRequirement}
                  onChange={(e) => setEditRequirement(e.target.value)}
                  autoSize={{ minRows: 4, maxRows: 6 }}
                  maxLength={2000}
                  showCount
                  placeholder="例如：将图片里的“年度总结”改成“项目复盘”，其他布局、背景、人物和图标都保持不变。"
                />
                <div className="dc-row">
                  <span>输出尺寸</span>
                  <Select value={size} onChange={setSize} options={sizeOptions} />
                </div>
                <div className="dc-row">
                  <span>生图模型</span>
                  <Select value={modelPreference} onChange={setModelPreference} options={modelOptions.length ? modelOptions : [{ value: 'default', label: '默认（自动切换）' }]} />
                </div>
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<SwapOutlined />}
                  loading={editLoading}
                  disabled={!task?.id || loading || !editRequirement.trim()}
                  onClick={handleImageEdit}
                >按需求重新生图</Button>
              </div>
            </div>
          )}

          <div className="dc-history">
            <div className="dc-history-title">历史作品{historyTotal ? ` · 共 ${historyTotal} 条` : ''}</div>
            <div className="dc-history-list">
              {history.length ? history.map(h => (
                <button key={h.id} className={`dc-history-row ${task?.id === h.id ? 'active' : ''}`} onClick={() => selectHistory(h)}>
                  <span className="ic">{h.type === 'prompt_generate' ? <PictureOutlined /> : <CloudUploadOutlined />}</span>
                  <span className="tt" title={taskTitle(h)}>{taskTitle(h)}</span>
                  <em className={`st st-${h.status}`}>{h.status === 'completed' ? '已完成' : h.status === 'failed' ? '失败' : '处理中'}</em>
                </button>
              )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无历史" />}
            </div>
            {historyTotal > HISTORY_PAGE_SIZE && (
              <div className="dc-history-pager">
                <Pagination
                  size="small"
                  simple
                  current={historyPage}
                  pageSize={HISTORY_PAGE_SIZE}
                  total={historyTotal}
                  onChange={(pg) => loadHistory(pg)}
                />
              </div>
            )}
          </div>
        </aside>

        <main className="dc-preview-panel">
          <div className="dc-preview-toolbar">
            <div>
              <strong>{task ? '图片预览' : '等待创作'}</strong>
              <span>{task?.imageModel ? `模型：${task.imageModel}` : '生成后可下载与识别文字'}</span>
            </div>
            <div className="dc-toolbar-actions">
              <Button icon={<EyeOutlined />} disabled={!currentImage} onClick={handleOcr} loading={ocrLoading}>识别文字</Button>
              <Button icon={<DownloadOutlined />} disabled={!currentImage} onClick={handleDownload}>下载</Button>
              <Popconfirm title="删除当前任务？" okText="删除" cancelText="取消" onConfirm={handleDeleteCurrent}>
                <Button danger icon={<DeleteOutlined />} disabled={!task?.id || loading}>删除</Button>
              </Popconfirm>
            </div>
          </div>
          <div className="dc-canvas-shell">
            {(loading || editLoading) && <div className="dc-loading"><Spin size="large" /><span>{editLoading ? '正在应用文字修改…' : '正在生成设计图…'}</span></div>}
            {!currentImage && !loading ? <Empty description="输入提示词或上传图片开始创作" /> : currentImage ? <img key={task?.resultAssetId || task?.assetId} src={currentImage} alt="设计图预览" /> : null}
          </div>
          {error && <div className="dc-error">{error}</div>}
        </main>

        <aside className="dc-panel dc-text-panel">
          <div className="dc-text-head">
            <div><HighlightOutlined /><strong>图片文字编辑</strong></div>
            <Tag color={textItems.length ? 'blue' : 'default'}>{textItems.length ? `${textItems.length} 条文字` : '未识别'}</Tag>
          </div>
          <p className="dc-text-desc">先识别图片中文字，再在右侧修改。点击应用后会调用图片编辑模型，尽量保持原图构图和风格，只替换文字。</p>
          <div className="dc-text-list">
            {textItems.length ? textItems.map((item, index) => (
              <div className="dc-text-item" key={item.id || index}>
                <div className="dc-text-original"><span>原文字</span><strong>{item.text}</strong></div>
                <Input value={item.to} onChange={(e) => setTextItems(prev => prev.map((t, i) => i === index ? { ...t, to: e.target.value } : t))} placeholder="修改为…" />
              </div>
            )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="点击识别后在这里编辑文字" />}
          </div>
          <Button type="primary" size="large" block icon={<SwapOutlined />} disabled={!canEdit} loading={editLoading} onClick={handleApplyEdits}>应用修改并生成新图</Button>
          <div className="dc-note">说明：AI 智能改字会尽量保留原图，但复杂海报可能出现轻微重绘差异。</div>
        </aside>
      </section>
      <Modal
        title="选择引用图片"
        open={refPickerOpen}
        onCancel={() => setRefPickerOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <div className="dc-ref-picker-grid dc-ref-picker-modal-grid">
          {refImages.map(img => (
            <button type="button" key={img.uuid} className="dc-ref-picker-item" onClick={() => insertRefImage(img.uuid)}>
              <img src={`/api/design-creator/${draftTaskId}/reference-images/${img.uuid}/view`} alt={img.name} />
              <span>{img.name}</span>
            </button>
          ))}
        </div>
        <div className="dc-ref-picker-foot">点击图片后会在提示词光标处插入 @img:uuid；生成时后端会读取原图并传给以图生图接口。</div>
      </Modal>
    </div>
  );
}
